/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

import React, { Suspense } from 'react';
import './App.css';
import { useStore } from './stores/common';
import * as Selector from './stores/selector';
import { App, ConfigProvider } from 'antd';
import ErrorPage from './ErrorPage';
import AppCreator from './appCreator';
import { Beforeunload } from 'react-beforeunload';
import Loading from './loading';
import { usePrimitiveStore } from './stores/commonPrimitive';

// The standalone routes below are each reachable by direct URL only, and no
// two ever render together — so keeping all eight in the entry chunk made
// every visitor download and compile pages they'd almost never open. Lazy
// them: a visitor to /terms now pays for the terms page, not the Commons
// gallery grid and the account page's Firestore queries. AppCreator itself
// stays eager because it is the route nearly everyone lands on; an extra
// round trip there would delay first paint for the common case.
const TermsOfService = React.lazy(() => import('./legal/termsOfService'));
const PrivacyPolicy = React.lazy(() => import('./legal/privacyPolicy'));
const Commons = React.lazy(() => import('./project/commons'));
const LessonCommons = React.lazy(() => import('./project/lessonCommons'));
const Tutorial = React.lazy(() => import('./project/tutorial'));
const About = React.lazy(() => import('./project/about'));
const CaseStudies = React.lazy(() => import('./project/caseStudies'));
const AccountPage = React.lazy(() => import('./accountPage'));
// Side-effect import: standalone routes (Commons, Tutorial, legal pages)
// short-circuit before AppCreator runs, which is where i18n is otherwise
// transitively imported. Without this line, useTranslation() on those routes
// would resolve against an uninitialized i18next instance.
import './i18n/i18n';

// Lightweight path-based "router" so external links to /aims/terms,
// /aims/privacy, /aims/commons, /aims/tutorial, /aims/about, and /aims/account
// land directly on those pages without booting the full modeling app. Firebase
// Hosting rewrites any path to index.html, so the SPA always loads; we just
// choose which view to render based on the URL. Trailing slash + optional
// .html for friendlier external links.
const matchStandalonePath = (
  pathname: string,
): 'terms' | 'privacy' | 'commons' | 'lesson-commons' | 'tutorial' | 'about' | 'case-studies' | 'account' | null => {
  const m = pathname.replace(/\/+$/, '').toLowerCase();
  if (m.endsWith('/terms') || m.endsWith('/terms.html')) return 'terms';
  if (m.endsWith('/privacy') || m.endsWith('/privacy.html')) return 'privacy';
  // Check lesson-commons before commons so the '/commons' suffix test doesn't
  // swallow '/lesson-commons'.
  if (m.endsWith('/lesson-commons') || m.endsWith('/lesson-commons.html')) return 'lesson-commons';
  if (m.endsWith('/commons') || m.endsWith('/commons.html')) return 'commons';
  if (m.endsWith('/tutorial') || m.endsWith('/tutorial.html')) return 'tutorial';
  if (m.endsWith('/about') || m.endsWith('/about.html')) return 'about';
  // Accept both /case-studies and /casestudies (no hyphen) for friendlier URLs.
  if (m.endsWith('/case-studies') || m.endsWith('/case-studies.html')) return 'case-studies';
  if (m.endsWith('/casestudies') || m.endsWith('/casestudies.html')) return 'case-studies';
  if (m.endsWith('/account') || m.endsWith('/account.html')) return 'account';
  return null;
};

const goToAppRoot = () => {
  // Send the user back to the main app. Use the configured base path so
  // we honor Vite's `base: '/aims'` setting in production.
  window.location.assign(import.meta.env.BASE_URL || '/');
};

const MyApp = React.memo(() => {
  const locale = useStore(Selector.locale);
  const params = new URLSearchParams(window.location.search);
  const viewOnly = params.get('viewonly') === 'true';
  const standalonePath = matchStandalonePath(window.location.pathname);

  return (
    <ConfigProvider locale={locale}>
      <App>
        <ErrorPage>
          <Suspense fallback={<Loading />}>
            {standalonePath === 'terms' ? (
              <TermsOfService close={goToAppRoot} standalone />
            ) : standalonePath === 'privacy' ? (
              <PrivacyPolicy close={goToAppRoot} standalone />
            ) : standalonePath === 'commons' ? (
              <Commons />
            ) : standalonePath === 'lesson-commons' ? (
              <LessonCommons />
            ) : standalonePath === 'tutorial' ? (
              <Tutorial />
            ) : standalonePath === 'about' ? (
              <About />
            ) : standalonePath === 'case-studies' ? (
              <CaseStudies />
            ) : standalonePath === 'account' ? (
              <AccountPage />
            ) : viewOnly ? (
              <AppCreator viewOnly={true} />
            ) : (
              <MyBeforeunload>
                <AppCreator viewOnly={false} />
              </MyBeforeunload>
            )}
          </Suspense>
        </ErrorPage>
      </App>
    </ConfigProvider>
  );
});

const MyBeforeunload = React.memo(({ children }: { children: React.ReactNode }) => {
  // CRITICAL: ALWAYS render the SAME element tree — <Beforeunload>{children}</…>.
  // The warning is gated INSIDE the handler (read at event time), NOT by
  // conditionally rendering the wrapper. Conditionally returning `children` vs
  // `<Beforeunload>{children}</Beforeunload>` changes the tree at this position,
  // so React UNMOUNTS and REMOUNTS all children (the entire AppCreator, the lab,
  // the WebGL canvas) whenever the condition flips — which reloaded the whole
  // project (CSV re-parse, lost GL context) on the first edit that set `changed`.
  const whiteList = ['gen8A3WDDHS2f9Y81muVUz1ZgJ33'];
  const isInWhiteList = whiteList.find((id) => id === useStore.getState().user.uid);
  return (
    <Beforeunload
      onBeforeunload={(event) => {
        // Dev whitelist: never prompt.
        if (!import.meta.env.PROD && !!isInWhiteList) return undefined;
        // Only warn when there are unsaved edits. Reading `changed` here (event
        // time) also keeps the guard correct when a dirty project sits under the
        // landing overlay (Help -> Welcome).
        if (!usePrimitiveStore.getState().changed) return undefined;
        // Returning a non-empty string + preventDefault() reliably shows the
        // native confirmation; the text itself is ignored by modern browsers.
        event.preventDefault();
        return 'You have unsaved changes.';
      }}
    >
      {children}
    </Beforeunload>
  );
});

export default MyApp;
